/**
 * Minimal fetch wrapper. The UI only talks to same-origin paths (`/api/<service>/…`) behind the
 * ingress, which forwards to the in-cluster services; the bearer token comes from the shell's
 * in-memory OIDC session and is never persisted.
 */
export const SERVICES = {
  orchestrator: "/api/orchestrator",
  cases: "/api/cases",
  audit: "/api/audit",
  memory: "/api/memory",
  gateway: "/api/gateway",
  metering: "/api/metering",
  controlPlane: "/api/control-plane",
  docintel: "/api/docintel",
  identity: "/api/identity",
  retrieval: "/api/retrieval",
  guardrails: "/api/guardrails",
  channels: "/api/channels",
  /** Evals service: agent test cases and test runs (ADR-0062). */
  evals: "/api/evals",
  connectorHost: "/api/connector-host",
  /** Work tools (ADR-0091): notifications and made files (their people-facing routes only). */
  notify: "/api/notify",
  documents: "/api/documents",
  /** Organisation data tables (ADR-0106). */
  datatables: "/api/datatables",
} as const;
export type Service = keyof typeof SERVICES;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Human-readable message from a service error body: FastAPI `{detail: string}`, FastAPI
 * validation `{detail: [{loc, msg}]}` and the gateway's OpenAI-style `{error: {message}}`.
 */
export function errorDetail(body: unknown): string {
  if (!body || typeof body !== "object") return "";
  const b = body as { detail?: unknown; error?: { message?: unknown } };
  if (typeof b.detail === "string") return b.detail;
  if (Array.isArray(b.detail)) {
    return b.detail
      .map((d: unknown) => {
        if (!d || typeof d !== "object") return String(d);
        const e = d as { loc?: unknown[]; msg?: unknown };
        const loc = (e.loc ?? []).filter((x) => x !== "body").join(".");
        return loc ? `${loc}: ${String(e.msg)}` : String(e.msg);
      })
      .join("; ");
  }
  if (b.detail !== undefined) return JSON.stringify(b.detail);
  if (b.error && typeof b.error.message === "string") return b.error.message;
  return "";
}

export type TokenGetter = () => string | null | Promise<string | null>;

export interface HttpOptions {
  getToken: TokenGetter;
  /** Origin prefix; empty (same origin) in the browser, absolute in Node tests. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** Called on 401 so the shell can renew or re-authenticate. */
  onUnauthorized?: () => void;
}

export type Query = Record<string, string | number | boolean | undefined | null>;

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

export class Http {
  constructor(private readonly opts: HttpOptions) {}

  url(service: Service, path: string): string {
    return `${this.opts.baseUrl ?? ""}${SERVICES[service]}${path}`;
  }

  async headers(extra?: HeadersInit): Promise<Headers> {
    const h = new Headers(extra);
    const token = await this.opts.getToken();
    if (token) h.set("Authorization", `Bearer ${token}`);
    return h;
  }

  async raw(service: Service, path: string, init: RequestInit = {}): Promise<Response> {
    const f = this.opts.fetchImpl ?? fetch;
    const res = await f(this.url(service, path), {
      ...init,
      headers: await this.headers(init.headers),
      credentials: "same-origin",
    });
    if (res.status === 401) this.opts.onUnauthorized?.();
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = undefined;
      }
      throw new ApiError(res.status, errorDetail(body) || res.statusText || `HTTP ${res.status}`, body);
    }
    return res;
  }

  async json<T>(service: Service, path: string, init: RequestInit = {}): Promise<T> {
    const res = await this.raw(service, path, {
      ...init,
      headers: { Accept: "application/json", ...(init.body && !(init.body instanceof FormData) ? { "Content-Type": "application/json" } : {}), ...init.headers },
    });
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  get<T>(service: Service, path: string, query?: Query): Promise<T> {
    return this.json<T>(service, withQuery(path, query));
  }

  post<T>(service: Service, path: string, body?: unknown, extra: RequestInit = {}): Promise<T> {
    return this.json<T>(service, path, {
      method: "POST",
      body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
      ...extra,
    });
  }

  put<T>(service: Service, path: string, body: unknown): Promise<T> {
    return this.json<T>(service, path, { method: "PUT", body: JSON.stringify(body) });
  }

  patch<T>(service: Service, path: string, body: unknown): Promise<T> {
    return this.json<T>(service, path, { method: "PATCH", body: JSON.stringify(body) });
  }

  delete<T>(service: Service, path: string): Promise<T> {
    return this.json<T>(service, path, { method: "DELETE" });
  }
}
